/**
 * Utility for logging image-related operations and detecting potential issues
 *
 * Implemented to solve the issue reported in JIRA EB-294788:
 * "Some Event Images are reportedly not loading or are sporadically being removed after changes are saved on the Build Event Page"
 *
 * This logging system tracks when and why event images disappear,
 * recording actions such as uploads, deletions, and display errors that could lead to
 * image loss in events.
 */

import { HAS_WINDOW } from '@eventbrite/feature-detection';
import logger from './index';

// Constants for image operation types
export const IMAGE_OPERATION_UPLOAD = 'upload';
export const IMAGE_OPERATION_DELETE = 'delete';
export const IMAGE_OPERATION_ERROR = 'error';

export const IMAGE_TYPE_EVENT_LOGO = 'image-event-logo';
export const IMAGE_TYPE_STRUCTURED_CONTENT = 'structured-content';

export interface ImageOperationDetails {
    component?: string;
    userTriggered?: boolean;
    reason?: string;
    errorType?: string;
    error?: any;
    errorStack?: string | null;
    hasOriginal?: boolean;
    isCoverImage?: boolean;
    originalUrl?: string;
    width?: number;
    height?: number;
    aspectRatio?: string;
    edgeColor?: string;
    cropMask?: any;
    fileType?: string;
    fileSize?: number;
    [key: string]: any;
}

export interface ImageOperationOptions {
    operation: string;
    imageId: string | null;
    imageUrl: string | null;
    imageType: string;
    eventId: string;
    userId?: string;
    pageUrl?: string;
    error?: Error | string;
    details?: ImageOperationDetails;
}

export interface ImageSnapshotData {
    id: string;
    url: string;
    type?: string;
    aspect_ratio?: string;
    original?: {
        url: string;
        width: number;
        height: number;
    };
    edge_color?: string;
    crop_mask?: any;
}

// Heap Analytics tracking function - Internal, not exported
const track = (data: any) => {
    if (HAS_WINDOW && window?.dataLayer) {
        window.dataLayer.push({
            event: 'track',
            sendToHeap: true,
            ...data,
        });
    }
};

// Logs an image-related operation
export const logImageOperation = (
    options: ImageOperationOptions,
): Promise<any> => {
    const logData = {
        operation: options.operation,
        timestamp: new Date().toISOString(),
        imageId: options.imageId || 'unknown',
        imageUrl: options.imageUrl || 'unknown',
        imageType: options.imageType || 'unknown',
        eventId: options.eventId || 'unknown',
        userId: options.userId || 'unknown',
        pageUrl:
            options.pageUrl || (HAS_WINDOW ? window.location.href : 'unknown'),
        userAgent: HAS_WINDOW ? navigator.userAgent : 'unknown',
        details: options.details || {},
    };

    if (options.error) {
        logData.details.error =
            options.error instanceof Error
                ? options.error.message
                : String(options.error);

        logData.details.errorStack =
            options.error instanceof Error ? options.error.stack : null;
    }

    // Log to Sentry via client logger
    if (options.error) {
        logger.error(`Image operation '${options.operation}' failed`, {
            imageId: options.imageId || 'unknown',
            imageUrl: options.imageUrl || 'unknown',
            imageType: options.imageType || 'unknown',
            eventId: options.eventId || 'unknown',
            timestamp: logData.timestamp,
            details: options.details || {},
            error:
                options.error instanceof Error
                    ? options.error
                    : new Error(String(options.error)),
            jiraTicket: 'EB-294788',
        });
    } else {
        const isImportantOperation =
            options.operation === IMAGE_OPERATION_DELETE ||
            options.operation === 'snapshot';

        if (isImportantOperation) {
            logger.info(`Image operation '${options.operation}' completed`, {
                imageId: options.imageId || 'unknown',
                imageUrl: options.imageUrl || 'unknown',
                imageType: options.imageType || 'unknown',
                eventId: options.eventId || 'unknown',
                timestamp: logData.timestamp,
                details: options.details || {},
                jiraTicket: 'EB-294788',
            });
        } else {
            logger.info(`Image operation '${options.operation}' tracked`, {
                imageId: options.imageId || 'unknown',
                imageUrl: options.imageUrl || 'unknown',
                imageType: options.imageType || 'unknown',
                eventId: options.eventId || 'unknown',
                jiraTicket: 'EB-294788',
            });
        }
    }

    // Track in Heap Analytics for reporting and monitoring
    try {
        track({
            eventName: `Image_${options.operation}_${
                options.error ? 'Error' : 'Success'
            }`,
            eventData: {
                category: 'Event Image Management',
                action: `Image ${options.operation}`,
                label: options.error ? 'Error' : 'Success',
                ...logData,
                details: JSON.stringify(logData.details),
                jiraTicket: 'EB-294788',
            },
        });
    } catch (e) {
        console.error('Failed to track image operation:', e);
        logger.error('Failed to track image operation in analytics', {
            error: e,
            originalOperation: options.operation,
            jiraTicket: 'EB-294788',
        });
    }

    return Promise.resolve(logData);
};

// Creates a record of an existing image for future reference
export const createImageSnapshot = (
    imageData: ImageSnapshotData,
    eventId: string,
): Promise<any> => {
    logger.info('Creating image snapshot for reference', {
        imageId: imageData.id || 'unknown',
        imageUrl: imageData.url || 'unknown',
        imageType: imageData.type || 'event-image',
        eventId: eventId || 'unknown',
        jiraTicket: 'EB-294788',
        aspectRatio: imageData.aspect_ratio,
        hasOriginal: !!imageData.original,
    });

    // Creates a record of the existing image for reference in case of issues
    return logImageOperation({
        operation: 'snapshot',
        imageId: imageData.id,
        imageUrl: imageData.url,
        imageType: imageData.type || 'event-image',
        eventId,
        details: {
            originalUrl: imageData.original?.url,
            width: imageData.original?.width,
            height: imageData.original?.height,
            aspectRatio: imageData.aspect_ratio,
            edgeColor: imageData.edge_color,
            cropMask: imageData.crop_mask,
        },
    });
};
